import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Sparkles, ChevronRight, Calendar, DollarSign, Sun, TrendingUp, 
  User, Shirt, Crown, Scissors, Footprints, Gem, Wind, Palette,
  Heart, Star, Zap, Check, Download, Lock, ShoppingBag, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";

// Style data
const EVENTS = [
  { id: "date-night", name: "Date Night", icon: Heart, emoji: "💕" },
  { id: "luxury-dinner", name: "Luxury Dinner", icon: Crown, emoji: "✨" },
  { id: "club-nightlife", name: "Club / Nightlife", icon: Zap, emoji: "🔥" },
  { id: "wedding-guest", name: "Wedding Guest", icon: Star, emoji: "💒" },
  { id: "fashion-vip", name: "Fashion Show / VIP", icon: Sparkles, emoji: "👑" },
  { id: "casual-chic", name: "Casual Chic / Errands", icon: Sun, emoji: "☀️" },
  { id: "business-boss", name: "Business / Boss Woman", icon: TrendingUp, emoji: "💼" },
  { id: "vacation-resort", name: "Vacation / Resort", icon: Calendar, emoji: "🏝️" },
];

const MOODS = [
  { id: "soft", name: "Soft & Feminine", color: "#FFB6C1" },
  { id: "bold", name: "Bold & Powerful", color: "#FF1493" },
  { id: "sexy", name: "Sexy & Confident", color: "#8B0000" },
  { id: "classy", name: "Classy & Elegant", color: "#C9B037" },
];

const BUDGETS = [
  { id: "affordable", name: "Affordable", symbol: "$", range: "Under $100" },
  { id: "mid-range", name: "Mid-Range", symbol: "$$", range: "$100 - $500" },
  { id: "luxury", name: "Luxury", symbol: "$$$", range: "$500+" },
];

const SEASONS = [
  { id: "winter", name: "Winter", colors: ["#1a1a2e", "#4a0e0e", "#2d2d44", "#c9b037"] },
  { id: "spring", name: "Spring", colors: ["#ffb6c1", "#98fb98", "#e6e6fa", "#ffefd5"] },
  { id: "summer", name: "Summer", colors: ["#fff8dc", "#e0ffff", "#fafad2", "#f0fff0"] },
  { id: "fall", name: "Fall", colors: ["#8b4513", "#d2691e", "#cd853f", "#daa520"] },
];

const TREND_LEVELS = [
  { id: "timeless", name: "Timeless Classic", desc: "Never goes out of style" },
  { id: "viral", name: "Viral / Trendy", desc: "TikTok & IG inspired" },
];

// Style Recommendations Database
const STYLE_RECOMMENDATIONS = {
  "date-night": {
    outfit: ["Satin slip dress", "Bodycon midi dress", "Elegant jumpsuit", "Blazer with mini skirt"],
    shoes: ["Strappy heels", "Pointed pumps", "Elegant mules"],
    hair: [
      { style: "Soft waves", vibe: "Romantic & feminine" },
      { style: "Sleek low bun", vibe: "Elegant & sophisticated" },
      { style: "Half-up half-down", vibe: "Effortlessly pretty" },
    ],
    jewelry: [
      { style: "Delicate gold layers", vibe: "Subtle & romantic" },
      { style: "Statement earrings", vibe: "Eye-catching" },
      { style: "Tennis bracelet", vibe: "Timeless elegance" },
    ],
    accessories: ["Small clutch", "Mini shoulder bag", "Delicate belt"],
    perfume: [
      { scent: "Vanilla & amber", vibe: "Warm & sensual" },
      { scent: "Rose & musk", vibe: "Romantic & feminine" },
    ],
    colors: ["Black", "Deep red", "Champagne", "Burgundy", "Navy"],
  },
  "luxury-dinner": {
    outfit: ["Structured blazer set", "Floor-length gown", "Satin midi dress", "Tailored co-ord set"],
    shoes: ["Pointed stilettos", "Embellished heels", "Satin pumps"],
    hair: [
      { style: "Sleek bun", vibe: "Elegant & refined" },
      { style: "Old Hollywood waves", vibe: "Glamorous" },
      { style: "Wet look slicked back", vibe: "High fashion" },
    ],
    jewelry: [
      { style: "Gold minimal pieces", vibe: "Rich quiet luxury" },
      { style: "Diamond studs", vibe: "Understated elegance" },
      { style: "Statement necklace", vibe: "Bold sophistication" },
    ],
    accessories: ["Structured clutch", "Mini top-handle bag"],
    perfume: [
      { scent: "Oud & amber", vibe: "Rich & luxurious" },
      { scent: "Jasmine & sandalwood", vibe: "Sophisticated" },
    ],
    colors: ["Black", "Champagne", "Deep red", "Chocolate", "Emerald"],
  },
  "club-nightlife": {
    outfit: ["Bodycon mini dress", "Crop top with leather pants", "Sequin set", "Cut-out dress"],
    shoes: ["Platform heels", "Knee-high boots", "Strappy stilettos"],
    hair: [
      { style: "High ponytail", vibe: "Fierce & fun" },
      { style: "Loose curls", vibe: "Glamorous" },
      { style: "Slicked back wet look", vibe: "Edgy" },
    ],
    jewelry: [
      { style: "Chunky hoops", vibe: "Bold & trendy" },
      { style: "Layered chains", vibe: "Street glam" },
      { style: "Body chain", vibe: "Daring" },
    ],
    accessories: ["Mini crossbody", "Belt bag", "Statement belt"],
    perfume: [
      { scent: "Sweet & fruity", vibe: "Fun & flirty" },
      { scent: "Spicy & bold", vibe: "Confident" },
    ],
    colors: ["Black", "Hot pink", "Silver", "Red", "Neon accents"],
  },
  "wedding-guest": {
    outfit: ["Flowy midi dress", "Elegant maxi dress", "Tailored suit", "Romantic wrap dress"],
    shoes: ["Block heels", "Elegant sandals", "Classic pumps"],
    hair: [
      { style: "Elegant updo", vibe: "Formal & polished" },
      { style: "Soft romantic curls", vibe: "Feminine" },
      { style: "Low chignon", vibe: "Classic elegance" },
    ],
    jewelry: [
      { style: "Pearl earrings", vibe: "Timeless" },
      { style: "Delicate gold set", vibe: "Refined" },
      { style: "Floral brooch", vibe: "Vintage charm" },
    ],
    accessories: ["Envelope clutch", "Fascinator/hat", "Pashmina wrap"],
    perfume: [
      { scent: "Floral bouquet", vibe: "Fresh & romantic" },
      { scent: "Light musk", vibe: "Subtle elegance" },
    ],
    colors: ["Blush", "Sage green", "Lavender", "Dusty blue", "Coral"],
  },
  "fashion-vip": {
    outfit: ["Avant-garde pieces", "Designer statement look", "Monochrome power suit", "Editorial dress"],
    shoes: ["Architectural heels", "Designer boots", "Statement platforms"],
    hair: [
      { style: "Sleek middle part", vibe: "Editorial" },
      { style: "Sculptural updo", vibe: "Avant-garde" },
      { style: "Wet look waves", vibe: "Fashion forward" },
    ],
    jewelry: [
      { style: "Sculptural pieces", vibe: "Artistic" },
      { style: "Oversized earrings", vibe: "Statement" },
      { style: "Cuff bracelets", vibe: "Bold" },
    ],
    accessories: ["Designer bag", "Sunglasses", "Statement belt"],
    perfume: [
      { scent: "Niche fragrance", vibe: "Unique & memorable" },
      { scent: "Leather & wood", vibe: "Edgy sophistication" },
    ],
    colors: ["All black", "All white", "Bold primary", "Metallics"],
  },
  "casual-chic": {
    outfit: ["Elevated basics", "Jeans with blazer", "Knit set", "Shirt dress"],
    shoes: ["Clean sneakers", "Loafers", "Ankle boots", "Ballet flats"],
    hair: [
      { style: "Messy bun", vibe: "Effortless" },
      { style: "Natural texture", vibe: "Relaxed" },
      { style: "Low ponytail", vibe: "Polished casual" },
    ],
    jewelry: [
      { style: "Everyday gold hoops", vibe: "Simple & chic" },
      { style: "Dainty necklace", vibe: "Understated" },
      { style: "Watch", vibe: "Practical elegance" },
    ],
    accessories: ["Tote bag", "Crossbody", "Baseball cap", "Scarf"],
    perfume: [
      { scent: "Fresh citrus", vibe: "Clean & energizing" },
      { scent: "Light floral", vibe: "Breezy" },
    ],
    colors: ["White", "Beige", "Denim blue", "Black", "Camel"],
  },
  "business-boss": {
    outfit: ["Power suit", "Pencil skirt with blouse", "Tailored dress", "Wide-leg trousers set"],
    shoes: ["Classic pumps", "Pointed flats", "Block heel boots"],
    hair: [
      { style: "Sleek blowout", vibe: "Professional" },
      { style: "Low bun", vibe: "Polished" },
      { style: "Straight & sleek", vibe: "Sharp" },
    ],
    jewelry: [
      { style: "Structured watch", vibe: "Powerful" },
      { style: "Simple studs", vibe: "Professional" },
      { style: "Thin gold bracelet", vibe: "Refined" },
    ],
    accessories: ["Structured tote", "Briefcase", "Leather belt"],
    perfume: [
      { scent: "Woody & musky", vibe: "Authoritative" },
      { scent: "Clean & fresh", vibe: "Confident" },
    ],
    colors: ["Navy", "Black", "Grey", "White", "Burgundy"],
  },
  "vacation-resort": {
    outfit: ["Flowy maxi dress", "Linen set", "Swimsuit cover-up", "Shorts with breezy top"],
    shoes: ["Sandals", "Espadrilles", "Slides", "Wedges"],
    hair: [
      { style: "Beachy waves", vibe: "Relaxed" },
      { style: "Braids", vibe: "Practical & cute" },
      { style: "Top knot", vibe: "Easy breezy" },
    ],
    jewelry: [
      { style: "Shell jewelry", vibe: "Beachy" },
      { style: "Colorful beads", vibe: "Fun & playful" },
      { style: "Gold anklet", vibe: "Bohemian" },
    ],
    accessories: ["Straw bag", "Wide-brim hat", "Sarong", "Sunglasses"],
    perfume: [
      { scent: "Coconut & vanilla", vibe: "Tropical" },
      { scent: "Ocean & citrus", vibe: "Fresh & breezy" },
    ],
    colors: ["White", "Turquoise", "Coral", "Yellow", "Palm prints"],
  },
};

export default function StylingSystemPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    event: null,
    mood: null,
    budget: null,
    season: null,
    trend: null,
  });
  const [showResults, setShowResults] = useState(false);
  const [purchasedProducts, setPurchasedProducts] = useState([]);
  const [loadingPurchase, setLoadingPurchase] = useState(null);
  const [checkingPurchase, setCheckingPurchase] = useState(false);

  // Check for purchase success on page load
  useEffect(() => {
    const purchaseStatus = searchParams.get('purchase');
    const sessionId = searchParams.get('session_id');
    const productId = searchParams.get('product');
    
    if (purchaseStatus === 'success' && sessionId && user) {
      verifyPurchase(sessionId);
    }
  }, [searchParams, user]);

  // Load user's purchased products
  useEffect(() => {
    if (user) {
      loadPurchasedProducts();
    }
  }, [user]);

  const loadPurchasedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/my-purchases`, {
        withCredentials: true
      });
      setPurchasedProducts(response.data.map(p => p.product_id));
    } catch (error) {
      console.error("Error loading purchases:", error);
    }
  };

  const verifyPurchase = async (sessionId) => {
    setCheckingPurchase(true);
    try {
      const response = await axios.get(`${API}/products/purchase-status/${sessionId}`, {
        withCredentials: true
      });
      
      if (response.data.payment_status === 'paid') {
        toast.success(`Thank you! ${response.data.product_name} is now available for download.`);
        loadPurchasedProducts();
        // Clear URL params
        window.history.replaceState({}, '', '/style-guide');
      }
    } catch (error) {
      console.error("Error verifying purchase:", error);
      toast.error("Could not verify purchase. Please contact support.");
    } finally {
      setCheckingPurchase(false);
    }
  };

  const handlePurchase = async (productId) => {
    if (!user) {
      toast.error("Please log in to purchase");
      return;
    }
    
    setLoadingPurchase(productId);
    try {
      const response = await axios.post(`${API}/products/checkout`, {
        product_id: productId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoadingPurchase(null);
    }
  };

  const handleDownload = async (productId) => {
    try {
      const response = await axios.get(`${API}/products/download/${productId}`, {
        withCredentials: true
      });
      
      toast.success(`Download ready: ${response.data.filename}`);
      // In production, this would open the actual PDF download
      // For now, show a message
      toast.info("PDF download would start here in production.");
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error.response?.data?.detail || "Download failed");
    }
  };

  const hasPurchased = (productId) => {
    return purchasedProducts.includes(productId);
  };

  const handleSelect = (key, value) => {
    setAnswers({ ...answers, [key]: value });
  };

  const nextStep = () => {
    if (step < 4) {
      setStep(step + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const resetQuiz = () => {
    setStep(0);
    setAnswers({ event: null, mood: null, budget: null, season: null, trend: null });
    setShowResults(false);
  };

  const getRecommendation = () => {
    if (!answers.event) return null;
    return STYLE_RECOMMENDATIONS[answers.event];
  };

  const recommendation = getRecommendation();

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="styling-system-page">
      <Navbar />

      <main className="pt-32 md:pt-40 pb-24">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="caption text-[#FF1493] mb-4">Digital Styling System</p>
            <h1 className="heading-lg mb-6">Style Match System™</h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              Get personalized styling recommendations based on your event, mood, budget, and more.
              Your complete look - from outfit to perfume.
            </p>
          </div>

          {!showResults ? (
            /* Quiz Section */
            <div className="max-w-3xl mx-auto">
              {/* Progress */}
              <div className="flex items-center justify-center gap-2 mb-12">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`h-2 w-16 ${i <= step ? "bg-[#FF1493]" : "bg-white/10"}`}
                  />
                ))}
              </div>

              {/* Step 0: Event */}
              {step === 0 && (
                <div className="animate-in">
                  <h2 className="heading-md text-center mb-8">What's the occasion?</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {EVENTS.map((event) => (
                      <button
                        key={event.id}
                        onClick={() => handleSelect("event", event.id)}
                        className={`p-6 border text-center hover:border-[#FF1493] transition-all ${
                          answers.event === event.id
                            ? "border-[#FF1493] bg-[#FF1493]/10"
                            : "border-white/10 bg-[#141414]"
                        }`}
                      >
                        <span className="text-3xl mb-3 block">{event.emoji}</span>
                        <span className="text-white text-sm">{event.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 1: Mood */}
              {step === 1 && (
                <div className="animate-in">
                  <h2 className="heading-md text-center mb-8">What's your mood?</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {MOODS.map((mood) => (
                      <button
                        key={mood.id}
                        onClick={() => handleSelect("mood", mood.id)}
                        className={`p-8 border text-center hover:border-[#FF1493] transition-all ${
                          answers.mood === mood.id
                            ? "border-[#FF1493] bg-[#FF1493]/10"
                            : "border-white/10 bg-[#141414]"
                        }`}
                      >
                        <div
                          className="w-8 h-8 rounded-full mx-auto mb-4"
                          style={{ backgroundColor: mood.color }}
                        />
                        <span className="text-white text-lg">{mood.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Budget */}
              {step === 2 && (
                <div className="animate-in">
                  <h2 className="heading-md text-center mb-8">What's your budget?</h2>
                  <div className="grid grid-cols-3 gap-4">
                    {BUDGETS.map((budget) => (
                      <button
                        key={budget.id}
                        onClick={() => handleSelect("budget", budget.id)}
                        className={`p-8 border text-center hover:border-[#FF1493] transition-all ${
                          answers.budget === budget.id
                            ? "border-[#FF1493] bg-[#FF1493]/10"
                            : "border-white/10 bg-[#141414]"
                        }`}
                      >
                        <span className="text-3xl text-[#FF1493] mb-2 block">{budget.symbol}</span>
                        <span className="text-white text-lg block">{budget.name}</span>
                        <span className="text-gray-500 text-sm">{budget.range}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: Season */}
              {step === 3 && (
                <div className="animate-in">
                  <h2 className="heading-md text-center mb-8">What season?</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {SEASONS.map((season) => (
                      <button
                        key={season.id}
                        onClick={() => handleSelect("season", season.id)}
                        className={`p-6 border text-center hover:border-[#FF1493] transition-all ${
                          answers.season === season.id
                            ? "border-[#FF1493] bg-[#FF1493]/10"
                            : "border-white/10 bg-[#141414]"
                        }`}
                      >
                        <div className="flex justify-center gap-1 mb-4">
                          {season.colors.map((color, i) => (
                            <div
                              key={i}
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <span className="text-white text-lg">{season.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Trend Level */}
              {step === 4 && (
                <div className="animate-in">
                  <h2 className="heading-md text-center mb-8">Your style preference?</h2>
                  <div className="grid grid-cols-2 gap-4">
                    {TREND_LEVELS.map((trend) => (
                      <button
                        key={trend.id}
                        onClick={() => handleSelect("trend", trend.id)}
                        className={`p-8 border text-center hover:border-[#FF1493] transition-all ${
                          answers.trend === trend.id
                            ? "border-[#FF1493] bg-[#FF1493]/10"
                            : "border-white/10 bg-[#141414]"
                        }`}
                      >
                        <span className="text-white text-xl block mb-2">{trend.name}</span>
                        <span className="text-gray-500 text-sm">{trend.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-12">
                <button
                  onClick={prevStep}
                  className={`btn-secondary ${step === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={step === 0}
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  className="btn-primary"
                  disabled={
                    (step === 0 && !answers.event) ||
                    (step === 1 && !answers.mood) ||
                    (step === 2 && !answers.budget) ||
                    (step === 3 && !answers.season) ||
                    (step === 4 && !answers.trend)
                  }
                >
                  {step === 4 ? "Get My Style" : "Next"}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            /* Results Section */
            <div className="animate-in">
              <div className="text-center mb-12">
                <h2 className="heading-lg mb-4">Your Perfect Look</h2>
                <p className="text-gray-400">
                  For {EVENTS.find((e) => e.id === answers.event)?.name} • {MOODS.find((m) => m.id === answers.mood)?.name} • {SEASONS.find((s) => s.id === answers.season)?.name}
                </p>
              </div>

              {recommendation && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Outfit */}
                  <div className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Shirt className="w-6 h-6 text-[#FF1493]" />
                      <h3 className="font-heading text-xl text-white">Outfit</h3>
                    </div>
                    <ul className="space-y-2">
                      {recommendation.outfit.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-300">
                          <Check className="w-4 h-4 text-[#FF1493]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Shoes */}
                  <div className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Footprints className="w-6 h-6 text-[#FF1493]" />
                      <h3 className="font-heading text-xl text-white">Shoes</h3>
                    </div>
                    <ul className="space-y-2">
                      {recommendation.shoes.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-300">
                          <Check className="w-4 h-4 text-[#FF1493]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Hair */}
                  <div className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Scissors className="w-6 h-6 text-[#FF1493]" />
                      <h3 className="font-heading text-xl text-white">Hair</h3>
                    </div>
                    <ul className="space-y-3">
                      {recommendation.hair.map((item, i) => (
                        <li key={i} className="text-gray-300">
                          <span className="text-white font-medium">{item.style}</span>
                          <span className="text-gray-500 text-sm block">{item.vibe}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Jewelry */}
                  <div className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Gem className="w-6 h-6 text-[#FF1493]" />
                      <h3 className="font-heading text-xl text-white">Jewelry</h3>
                    </div>
                    <ul className="space-y-3">
                      {recommendation.jewelry.map((item, i) => (
                        <li key={i} className="text-gray-300">
                          <span className="text-white font-medium">{item.style}</span>
                          <span className="text-gray-500 text-sm block">{item.vibe}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Perfume */}
                  <div className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Wind className="w-6 h-6 text-[#FF1493]" />
                      <h3 className="font-heading text-xl text-white">Perfume</h3>
                    </div>
                    <ul className="space-y-3">
                      {recommendation.perfume.map((item, i) => (
                        <li key={i} className="text-gray-300">
                          <span className="text-white font-medium">{item.scent}</span>
                          <span className="text-gray-500 text-sm block">{item.vibe}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Color Palette */}
                  <div className="bg-[#141414] border border-white/10 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Palette className="w-6 h-6 text-[#FF1493]" />
                      <h3 className="font-heading text-xl text-white">Colors</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recommendation.colors.map((color, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-white/10 text-gray-300 text-sm"
                        >
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
                <button onClick={resetQuiz} className="btn-secondary">
                  Start Over
                </button>
                <Link to="/studio">
                  <button className="btn-primary">
                    <Sparkles className="w-4 h-4" />
                    Try On Outfits Now
                  </button>
                </Link>
              </div>

              {/* Pro Tip */}
              <div className="mt-16 p-8 bg-[#FF1493]/10 border border-[#FF1493]/30 text-center">
                <h3 className="font-heading text-2xl text-white mb-4">Pro Tip</h3>
                <p className="text-gray-300 max-w-2xl mx-auto">
                  <strong>Jewelry Rule:</strong> Heavy outfit → minimal jewelry. Simple outfit → statement jewelry.
                  <br /><br />
                  <strong>Perfume Match:</strong> Sexy vibes → vanilla/amber. Fresh vibes → citrus/floral. Boss vibes → woody/musky.
                </p>
              </div>
            </div>
          )}

          {/* Style Guides Section */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <p className="caption text-[#FF1493] mb-4">Style Guides</p>
              <h2 className="heading-lg">Master Your Look</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Color Palette System */}
              <div className="bg-[#141414] border border-white/10 p-8 hover-lift">
                <Palette className="w-10 h-10 text-[#FF1493] mb-4" />
                <h3 className="font-heading text-xl text-white mb-3">Color Palette System</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Match colors to your skin tone and season for maximum impact.
                </p>
                <ul className="text-gray-500 text-sm space-y-1">
                  <li>• Winter: Dark & rich tones</li>
                  <li>• Summer: Light & airy</li>
                  <li>• Fall: Warm neutrals</li>
                  <li>• Spring: Fresh tones</li>
                </ul>
              </div>

              {/* Hair x Outfit Guide */}
              <div className="bg-[#141414] border border-white/10 p-8 hover-lift">
                <Scissors className="w-10 h-10 text-[#FF1493] mb-4" />
                <h3 className="font-heading text-xl text-white mb-3">Hair x Outfit Match</h3>
                <p className="text-gray-400 text-sm mb-4">
                  The right hairstyle elevates your entire look.
                </p>
                <ul className="text-gray-500 text-sm space-y-1">
                  <li>• Blazer look → sleek hair/ponytail</li>
                  <li>• Streetwear → messy bun/braids</li>
                  <li>• Glam dress → curls/waves</li>
                </ul>
              </div>

              {/* Shoe Psychology */}
              <div className="bg-[#141414] border border-white/10 p-8 hover-lift">
                <Footprints className="w-10 h-10 text-[#FF1493] mb-4" />
                <h3 className="font-heading text-xl text-white mb-3">Shoe Psychology</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Your shoes send a message before you speak.
                </p>
                <ul className="text-gray-500 text-sm space-y-1">
                  <li>• Heels = power & femininity</li>
                  <li>• Sneakers = relaxed & cool</li>
                  <li>• Boots = dominance & edge</li>
                </ul>
              </div>

              {/* Jewelry Balancing */}
              <div className="bg-[#141414] border border-white/10 p-8 hover-lift">
                <Gem className="w-10 h-10 text-[#FF1493] mb-4" />
                <h3 className="font-heading text-xl text-white mb-3">Jewelry Balancing</h3>
                <p className="text-gray-400 text-sm mb-4">
                  The golden rule of accessorizing.
                </p>
                <ul className="text-gray-500 text-sm space-y-1">
                  <li>• Heavy outfit → minimal jewelry</li>
                  <li>• Simple outfit → statement pieces</li>
                  <li>• Match metals to your undertone</li>
                </ul>
              </div>

              {/* Perfume Matching */}
              <div className="bg-[#141414] border border-white/10 p-8 hover-lift">
                <Wind className="w-10 h-10 text-[#FF1493] mb-4" />
                <h3 className="font-heading text-xl text-white mb-3">Perfume Matching</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Complete your vibe with the perfect scent.
                </p>
                <ul className="text-gray-500 text-sm space-y-1">
                  <li>• Sexy → vanilla & amber</li>
                  <li>• Fresh → citrus & floral</li>
                  <li>• Boss → woody & musky</li>
                </ul>
              </div>

              {/* Event Styling */}
              <div className="bg-[#141414] border border-white/10 p-8 hover-lift">
                <Calendar className="w-10 h-10 text-[#FF1493] mb-4" />
                <h3 className="font-heading text-xl text-white mb-3">Event Styling</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Dress perfectly for every occasion.
                </p>
                <ul className="text-gray-500 text-sm space-y-1">
                  <li>• Date night essentials</li>
                  <li>• Business boss looks</li>
                  <li>• Wedding guest rules</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Digital Products Pricing Section */}
          <div className="mt-24" id="pricing" data-testid="pricing-section">
            <div className="text-center mb-12">
              <p className="caption text-[#FF1493] mb-4">Digital Style Guides</p>
              <h2 className="heading-lg mb-4">Get Your Complete Style System</h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Downloadable PDF guides with everything you need to master your personal style.
                One-time purchase, lifetime access.
              </p>
            </div>

            {checkingPurchase && (
              <div className="text-center py-8">
                <div className="w-8 h-8 spinner mx-auto mb-4"></div>
                <p className="text-gray-400">Verifying your purchase...</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Starter Package */}
              <div className="bg-[#141414] border border-white/10 p-8 flex flex-col" data-testid="pricing-starter">
                <div className="mb-6">
                  <FileText className="w-10 h-10 text-[#FF1493] mb-4" />
                  <h3 className="font-heading text-2xl text-white mb-2">Starter</h3>
                  <p className="text-gray-400 text-sm">Essential style foundations</p>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-gray-500 line-through text-lg">$49</span>
                    <span className="text-4xl font-heading text-white">$39</span>
                  </div>
                  <p className="text-gray-500 text-sm">One-time payment</p>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Style by Situation Guide (8 occasions)</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Color Palette System</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Basic Outfit Formulas</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Shoe Psychology Guide</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Jewelry Balancing Rules</span>
                  </li>
                </ul>

                {hasPurchased('starter') ? (
                  <button 
                    onClick={() => handleDownload('starter')}
                    className="btn-primary w-full"
                    data-testid="download-starter-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                ) : (
                  <button 
                    onClick={() => handlePurchase('starter')}
                    disabled={loadingPurchase === 'starter' || !user}
                    className="btn-secondary w-full"
                    data-testid="buy-starter-btn"
                  >
                    {loadingPurchase === 'starter' ? (
                      <div className="w-4 h-4 spinner" />
                    ) : !user ? (
                      <>
                        <Lock className="w-4 h-4" />
                        Login to Purchase
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        Get Starter Guide
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Pro Package - Featured */}
              <div className="bg-[#141414] border-2 border-[#FF1493] p-8 flex flex-col relative" data-testid="pricing-pro">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FF1493] px-4 py-1">
                  <span className="text-white text-xs font-mono uppercase tracking-wider">Most Popular</span>
                </div>
                
                <div className="mb-6">
                  <Crown className="w-10 h-10 text-[#FF1493] mb-4" />
                  <h3 className="font-heading text-2xl text-white mb-2">Pro</h3>
                  <p className="text-gray-400 text-sm">Complete styling system</p>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-gray-500 line-through text-lg">$149</span>
                    <span className="text-4xl font-heading text-white">$99</span>
                  </div>
                  <p className="text-gray-500 text-sm">One-time payment</p>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Everything in Starter</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Style Match System™ Results</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Seasonal Wardrobe Planning</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Hair x Outfit Combinations</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Perfume Matching Guide</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Body Type Styling Tips</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Trend vs Timeless Analysis</span>
                  </li>
                </ul>

                {hasPurchased('pro') ? (
                  <button 
                    onClick={() => handleDownload('pro')}
                    className="btn-primary w-full"
                    data-testid="download-pro-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                ) : (
                  <button 
                    onClick={() => handlePurchase('pro')}
                    disabled={loadingPurchase === 'pro' || !user}
                    className="btn-primary w-full"
                    data-testid="buy-pro-btn"
                  >
                    {loadingPurchase === 'pro' ? (
                      <div className="w-4 h-4 spinner" />
                    ) : !user ? (
                      <>
                        <Lock className="w-4 h-4" />
                        Login to Purchase
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        Get Pro System
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* VIP Package */}
              <div className="bg-[#141414] border border-white/10 p-8 flex flex-col" data-testid="pricing-vip">
                <div className="mb-6">
                  <Star className="w-10 h-10 text-[#FF1493] mb-4" />
                  <h3 className="font-heading text-2xl text-white mb-2">VIP</h3>
                  <p className="text-gray-400 text-sm">The ultimate collection</p>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-gray-500 line-through text-lg">$299</span>
                    <span className="text-4xl font-heading text-white">$249</span>
                  </div>
                  <p className="text-gray-500 text-sm">One-time payment</p>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Everything in Pro</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Personal Color Analysis Guide</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Capsule Wardrobe Builder</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Shopping Budget Templates</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Exclusive Lookbook (50+ Outfits)</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Seasonal Trend Reports (4x/year)</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-300 text-sm">
                    <Check className="w-4 h-4 text-[#FF1493] mt-0.5 flex-shrink-0" />
                    <span>Priority Styling Tips Updates</span>
                  </li>
                </ul>

                {hasPurchased('vip') ? (
                  <button 
                    onClick={() => handleDownload('vip')}
                    className="btn-primary w-full"
                    data-testid="download-vip-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download PDF
                  </button>
                ) : (
                  <button 
                    onClick={() => handlePurchase('vip')}
                    disabled={loadingPurchase === 'vip' || !user}
                    className="btn-secondary w-full"
                    data-testid="buy-vip-btn"
                  >
                    {loadingPurchase === 'vip' ? (
                      <div className="w-4 h-4 spinner" />
                    ) : !user ? (
                      <>
                        <Lock className="w-4 h-4" />
                        Login to Purchase
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4" />
                        Get VIP Collection
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Purchase benefits */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="p-4">
                <Download className="w-6 h-6 text-[#FF1493] mx-auto mb-2" />
                <p className="text-white font-medium">Instant Download</p>
                <p className="text-gray-500 text-sm">Get access immediately after purchase</p>
              </div>
              <div className="p-4">
                <FileText className="w-6 h-6 text-[#FF1493] mx-auto mb-2" />
                <p className="text-white font-medium">Lifetime Access</p>
                <p className="text-gray-500 text-sm">Download anytime, keep forever</p>
              </div>
              <div className="p-4">
                <Sparkles className="w-6 h-6 text-[#FF1493] mx-auto mb-2" />
                <p className="text-white font-medium">Free Updates</p>
                <p className="text-gray-500 text-sm">Get new content as we add it</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-24 text-center py-16 bg-[#141414] border border-white/10">
            <h2 className="heading-md mb-4">Ready to See Yourself in Style?</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              Use our Virtual Try-On to see how TailorGlam outfits look on you.
            </p>
            <Link to="/studio">
              <button className="btn-primary">
                <Sparkles className="w-4 h-4" />
                Try On Now
              </button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
